import { LoadingButton } from "@mui/lab";
import { DialogTitle } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { ProjectType } from "@repo/server-api";
import { useTranslation } from "next-i18next";
import React, { useState, forwardRef, useImperativeHandle, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { ProjectCreate } from "../components";
import { useCreateNewProject } from "../hooks/CreateNewProjectHook";
import { useNewProjectValidator } from "../hooks/ProjectValidationHooks";
import { createEmptyNewProject } from "../types/ProjectTypes";

import type { NewProject } from "../types/ProjectTypes";

export type DialogProjectCreateShowArgs = {
	project?: NewProject | null;
	contactIsReadOnly?: boolean;
};

export type DialogProjectCreateShowArgsHandle = {
	show: (args: DialogProjectCreateShowArgs) => void;
	close: () => void;
};

const DialogProjectCreate = forwardRef<DialogProjectCreateShowArgsHandle>((_props, ref) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [open, setOpen] = useState(false);
	const [project, setProject] = useState<NewProject>(createEmptyNewProject());
	const { errors, setErrors, validate } = useNewProjectValidator();
	const [contactIsReadOnly, setContactIsReadOnly] = useState(false);

	const { save, loading } = useCreateNewProject({
		onSaved: (id: string) => {
			handleCloseInternal();
			navigate(`/projects/${encodeURIComponent(id)}`);
		},
	});

	const handleCloseInternal = useCallback(() => {
		setOpen(false);
		setErrors({});
	}, [setErrors]);

	useImperativeHandle(
		ref,
		() => ({
			show: (args: DialogProjectCreateShowArgs) => {
				if (args.project) {
					setProject(args.project);
				} else {
					setProject(createEmptyNewProject());
				}

				setContactIsReadOnly(false);
				setOpen(true);
			},
			close: () => {
				handleCloseInternal();
			},
		}),
		[handleCloseInternal],
	);

	const handleSubmit = async () => {
		const result = await validate(project);
		if (!result.isValid) {
			return;
		}

		save(project);
	};

	return (
		<Dialog open={open} maxWidth="sm" fullWidth data-testid="project-create-dialog">
			<DialogTitle>
				{project.type === ProjectType.Tender ? t("new-tender") : t("new-project")}
			</DialogTitle>
			<DialogContent>
				<ProjectCreate
					project={project}
					setProject={setProject}
					errors={errors}
					setErrors={setErrors}
					contactIsReadOnly={contactIsReadOnly}
				/>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={handleCloseInternal} disabled={loading} autoFocus>
					{t("cancel")}
				</Button>
				<LoadingButton
					loading={loading}
					className={loading ? "" : "blueButton"}
					variant="contained"
					data-testid="save-button"
					onClick={handleSubmit}>
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
});

DialogProjectCreate.displayName = "DialogProjectCreate";
export { DialogProjectCreate };
